<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Manage Leave Types</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="/">Home</a></li>
                    <li class="breadcrumb-item">Leave & Attendance</li>
                    <li class="breadcrumb-item active">Leave Types</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Stats Cards -->
          <div class="row">
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Leave Types</p>
                      <h5 class="mb-0">{{ metrics.total }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                        <i class="mdi mdi-format-list-bulleted"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Active</p>
                      <h5 class="mb-0">{{ metrics.active }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                        <i class="mdi mdi-check-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Inactive</p>
                      <h5 class="mb-0">{{ metrics.inactive }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-danger rounded-circle text-white font-size-20">
                        <i class="mdi mdi-close-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Paid Leave Types</p>
                      <h5 class="mb-0">{{ metrics.paid }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                        <i class="mdi mdi-cash"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Card -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="search-box" style="min-width: 300px;">
                      <div class="position-relative">
                        <input
                            v-model="searchValue"
                            type="text"
                            class="form-control bg-light border-light rounded"
                            placeholder="Search leave types..."
                            @keyup.enter="fetchLeaveTypes"
                        >
                        <i class="bx bx-search-alt search-icon"></i>
                      </div>
                    </div>

                    <div class="d-flex align-items-center gap-2">
                      <!-- Status Filter -->
                      <select class="form-select" style="width: 150px;" v-model="statusFilter" @change="fetchLeaveTypes">
                        <option value="">All Status</option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>

                      <!-- Add New Button -->
                      <button class="btn btn-success" @click="openAddModal">
                        <i class="mdi mdi-plus-circle me-1"></i>
                        Add Leave Type
                      </button>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Loading -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2">Loading leave types...</p>
                  </div>

                  <!-- Empty State -->
                  <div v-else-if="leaveTypes.length === 0" class="text-center py-5">
                    <i class="mdi mdi-file-document-outline" style="font-size: 3rem; color: #ccc;"></i>
                    <p class="mt-2 text-muted">No leave types found</p>
                    <button class="btn btn-primary" @click="openAddModal">
                      <i class="mdi mdi-plus me-1"></i>
                      Add Your First Leave Type
                    </button>
                  </div>

                  <!-- Table -->
                  <div v-else class="table-responsive">
                    <table class="table align-middle table-hover">
                      <thead class="table-light">
                      <tr>
                        <th>#</th>
                        <th>Leave Type</th>
                        <th class="text-center">Allocation Days</th>
                        <th class="text-center">Paid</th>
                        <th class="text-center">Requires Approval</th>
                        <th class="text-center">Carry Forward</th>
                        <th class="text-center">Max Carry Days</th>
                        <th class="text-center">Status</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(item, index) in leaveTypes" :key="item.id">
                        <td>{{ index + 1 }}</td>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2">
                                <span class="avatar-title rounded-circle" :style="{ backgroundColor: item.color || '#5b73e8' }">
                                  {{ getInitials(item.typeName) }}
                                </span>
                            </div>
                            <div>
                              <h6 class="mb-0">{{ item.typeName }}</h6>
                              <small class="text-muted" v-if="item.description">{{ item.description }}</small>
                            </div>
                          </div>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-primary">{{ item.allocationDays }} days</span>
                        </td>
                        <td class="text-center">
                            <span class="badge" :class="item.isPaid === '1' ? 'bg-success' : 'bg-secondary'">
                              {{ item.isPaid === '1' ? 'Yes' : 'No' }}
                            </span>
                        </td>
                        <td class="text-center">
                            <span class="badge" :class="item.requiresApproval === '1' ? 'bg-warning' : 'bg-secondary'">
                              {{ item.requiresApproval === '1' ? 'Yes' : 'No' }}
                            </span>
                        </td>
                        <td class="text-center">
                            <span class="badge" :class="item.allowCarryForward === '1' ? 'bg-info' : 'bg-secondary'">
                              {{ item.allowCarryForward === '1' ? 'Yes' : 'No' }}
                            </span>
                        </td>
                        <td class="text-center">
                          {{ item.maxCarryForwardDays || '-' }}
                        </td>
                        <td class="text-center">
                          <div class="form-check form-switch d-flex justify-content-center">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :checked="item.isActive === '1'"
                                @change="toggleStatus(item)"
                                :disabled="item.toggling"
                            >
                          </div>
                        </td>
                        <td class="text-center">
                          <div class="d-flex justify-content-center gap-1">
                            <button
                                class="btn btn-sm btn-info"
                                @click="openEditModal(item)"
                                title="Edit"
                            >
                              <i class="mdi mdi-pencil"></i>
                            </button>
                            <button
                                class="btn btn-sm btn-danger"
                                @click="confirmDelete(item)"
                                title="Delete"
                            >
                              <i class="mdi mdi-delete"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div class="modal fade" id="leave-type-modal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header" :class="isEditing ? 'bg-info' : 'bg-success'" style="color: white;">
            <h5 class="modal-title">
              <i :class="isEditing ? 'mdi mdi-pencil' : 'mdi mdi-plus-circle'" class="me-2"></i>
              {{ isEditing ? 'Edit Leave Type' : 'Add New Leave Type' }}
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="saveLeaveType">
            <div class="modal-body">
              <!-- Leave Type Name -->
              <div class="mb-3">
                <label class="form-label">Leave Type Name <span class="text-danger">*</span></label>
                <input
                    type="text"
                    class="form-control"
                    v-model="formData.typeName"
                    placeholder="e.g., Annual Leave, Sick Leave"
                    required
                >
              </div>

              <!-- Description -->
              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea
                    class="form-control"
                    v-model="formData.description"
                    rows="2"
                    placeholder="Brief description of this leave type"
                ></textarea>
              </div>

              <!-- Allocation Days -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Allocation Days <span class="text-danger">*</span></label>
                  <input
                      type="number"
                      class="form-control"
                      v-model="formData.allocationDays"
                      min="0"
                      step="0.5"
                      required
                  >
                  <small class="text-muted">Days allocated per year</small>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Color</label>
                  <input
                      type="color"
                      class="form-control form-control-color w-100"
                      v-model="formData.color"
                  >
                </div>
              </div>

              <!-- Toggles Row 1 -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="form-check form-switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="isPaid"
                        v-model="formData.isPaid"
                    >
                    <label class="form-check-label" for="isPaid">Paid Leave</label>
                  </div>
                  <small class="text-muted">Employee gets paid during leave</small>
                </div>
                <div class="col-md-6">
                  <div class="form-check form-switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="requiresApproval"
                        v-model="formData.requiresApproval"
                    >
                    <label class="form-check-label" for="requiresApproval">Requires Approval</label>
                  </div>
                  <small class="text-muted">Manager approval needed</small>
                </div>
              </div>

              <!-- Toggles Row 2 -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <div class="form-check form-switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="allowCarryForward"
                        v-model="formData.allowCarryForward"
                    >
                    <label class="form-check-label" for="allowCarryForward">Allow Carry Forward</label>
                  </div>
                  <small class="text-muted">Unused days carry to next year</small>
                </div>
                <div class="col-md-6" v-if="formData.allowCarryForward">
                  <label class="form-label">Max Carry Forward Days</label>
                  <input
                      type="number"
                      class="form-control"
                      v-model="formData.maxCarryForwardDays"
                      min="0"
                      placeholder="e.g., 5"
                  >
                </div>
              </div>

              <!-- Active Status -->
              <div class="mb-3">
                <div class="form-check form-switch">
                  <input
                      class="form-check-input"
                      type="checkbox"
                      id="isActive"
                      v-model="formData.isActive"
                  >
                  <label class="form-check-label" for="isActive">Active</label>
                </div>
                <small class="text-muted">Inactive leave types cannot be used for applications</small>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="submit" class="btn" :class="isEditing ? 'btn-info' : 'btn-success'" :disabled="saving">
                <span v-if="saving">
                  <span class="spinner-border spinner-border-sm me-1"></span>
                  Saving...
                </span>
                <span v-else>
                  <i class="mdi mdi-check me-1"></i>
                  {{ isEditing ? 'Update' : 'Save' }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="delete-modal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title text-white">
              <i class="mdi mdi-alert me-2"></i>
              Confirm Delete
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body text-center py-4">
            <i class="mdi mdi-delete-alert text-danger" style="font-size: 48px;"></i>
            <h5 class="mt-3">Are you sure?</h5>
            <p class="text-muted">
              You are about to delete <strong>{{ deleteItem?.typeName }}</strong>.
              This action cannot be undone.
            </p>

            <!-- Force Delete Warning -->
            <div v-if="forceDeleteRequired" class="alert alert-danger text-start py-2">
              <i class="mdi mdi-alert-circle me-1"></i>
              <strong>Warning:</strong> This leave type has:
              <ul class="mb-2 mt-1">
                <li v-if="deleteStats.requestCount > 0">{{ deleteStats.requestCount }} leave request(s)</li>
                <li v-if="deleteStats.balanceCount > 0">{{ deleteStats.balanceCount }} leave balance(s)</li>
              </ul>
              <p class="mb-0">Type <strong>FORCE DELETE</strong> below to permanently delete everything:</p>
            </div>

            <!-- Force Delete Input -->
            <div v-if="forceDeleteRequired" class="mb-3">
              <input
                  type="text"
                  class="form-control text-center"
                  v-model="forceDeleteText"
                  placeholder="Type FORCE DELETE to confirm"
                  :class="{'is-valid': forceDeleteText === 'FORCE DELETE', 'is-invalid': forceDeleteText && forceDeleteText !== 'FORCE DELETE'}"
              >
            </div>

            <div v-else class="alert alert-warning py-2">
              <i class="mdi mdi-information me-1"></i>
              Employees with existing balances for this leave type may be affected.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="resetDeleteModal">Cancel</button>
            <button
                type="button"
                class="btn btn-danger"
                @click="deleteLeaveType"
                :disabled="deleting || (forceDeleteRequired && forceDeleteText !== 'FORCE DELETE')"
            >
              <span v-if="deleting">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Deleting...
              </span>
              <span v-else>
                <i class="mdi mdi-delete me-1"></i>
                {{ forceDeleteRequired ? 'Force Delete' : 'Yes, Delete' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <SuccessModal
        title="Leave Type"
        heading="Success!"
        :message="successMessage"
    />

    <!-- Error Modal -->
    <ErrorModal
        title="Error"
        heading="Operation Failed"
        :message="errorMessage"
    />
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import SuccessModal from "./modals/SuccessModal.vue";
import ErrorModal from "./modals/ErrorModal.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "LeaveTypesManagement",
  components: {
    NavigationBar,
    SuccessModal,
    ErrorModal
  },

  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      searchValue: '',
      statusFilter: '',
      leaveTypes: [],
      metrics: {
        total: 0,
        active: 0,
        inactive: 0,
        paid: 0
      },
      isEditing: false,
      formData: {
        id: null,
        typeName: '',
        description: '',
        allocationDays: 21,
        color: '#5b73e8',
        isPaid: true,
        requiresApproval: true,
        allowCarryForward: true,
        maxCarryForwardDays: 5,
        isActive: true
      },
      deleteItem: null,
      forceDeleteRequired: false,
      forceDeleteText: '',
      deleteStats: {
        requestCount: 0,
        balanceCount: 0
      },
      successMessage: '',
      errorMessage: ''
    };
  },

  mounted() {
    this.fetchLeaveTypes();
  },

  methods: {
    // Fetch leave types
    async fetchLeaveTypes() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getLeaveTypesManagement");
      data.append("search", this.searchValue);
      data.append("status", this.statusFilter);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.leaveTypes = res.data.data.leaveTypes.map(item => ({
            ...item,
            toggling: false
          }));
          this.metrics = res.data.data.metrics;
        }
      } catch (error) {
        console.error('Error fetching leave types:', error);
        this.showError('Failed to load leave types');
      } finally {
        this.loading = false;
      }
    },

    // Get initials
    getInitials(name) {
      if (!name) return 'LT';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    // Open add modal
    openAddModal() {
      this.isEditing = false;
      this.formData = {
        id: null,
        typeName: '',
        description: '',
        allocationDays: 21,
        color: '#5b73e8',
        isPaid: true,
        requiresApproval: true,
        allowCarryForward: true,
        maxCarryForwardDays: 5,
        isActive: true
      };
      $('#leave-type-modal').modal('show');
    },

    // Open edit modal
    openEditModal(item) {
      this.isEditing = true;
      this.formData = {
        id: item.id,
        typeName: item.typeName,
        description: item.description || '',
        allocationDays: item.allocationDays,
        color: item.color || '#5b73e8',
        isPaid: item.isPaid === '1',
        requiresApproval: item.requiresApproval === '1',
        allowCarryForward: item.allowCarryForward === '1',
        maxCarryForwardDays: item.maxCarryForwardDays || 5,
        isActive: item.isActive === '1'
      };
      $('#leave-type-modal').modal('show');
    },

    // Save leave type
    async saveLeaveType() {
      if (!this.formData.typeName) {
        this.showError('Leave Type Name is required');
        return;
      }

      if (!this.formData.allocationDays && this.formData.allocationDays !== 0) {
        this.showError('Allocation Days is required');
        return;
      }

      this.saving = true;

      const data = new FormData();
      data.append("function", this.isEditing ? "updateLeaveType" : "addLeaveType");

      if (this.isEditing) {
        data.append("id", this.formData.id);
      }

      data.append("typeName", this.formData.typeName);
      data.append("description", this.formData.description);
      data.append("allocationDays", this.formData.allocationDays);
      data.append("color", this.formData.color);
      data.append("isPaid", this.formData.isPaid ? '1' : '0');
      data.append("requiresApproval", this.formData.requiresApproval ? '1' : '0');
      data.append("allowCarryForward", this.formData.allowCarryForward ? '1' : '0');
      data.append("maxCarryForwardDays", this.formData.allowCarryForward ? this.formData.maxCarryForwardDays : 0);
      data.append("isActive", this.formData.isActive ? '1' : '0');

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#leave-type-modal').modal('hide');
          this.showSuccess(res.data.message);
          await this.fetchLeaveTypes();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error saving leave type:', error);
        this.showError('Failed to save leave type');
      } finally {
        this.saving = false;
      }
    },

    // Toggle active status
    async toggleStatus(item) {
      item.toggling = true;

      const newStatus = item.isActive === '1' ? '0' : '1';

      const data = new FormData();
      data.append("function", "toggleLeaveTypeStatus");
      data.append("id", item.id);
      data.append("isActive", newStatus);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          item.isActive = newStatus;
          this.showSuccess(res.data.message);
          await this.fetchLeaveTypes();
        } else {
          this.showError(res.data.message);
        }
      } catch (error) {
        console.error('Error toggling status:', error);
        this.showError('Failed to update status');
      } finally {
        item.toggling = false;
      }
    },

    // Confirm delete
    confirmDelete(item) {
      this.deleteItem = item;
      this.forceDeleteRequired = false;
      this.forceDeleteText = '';
      this.deleteStats = { requestCount: 0, balanceCount: 0 };
      $('#delete-modal').modal('show');
    },

    // Reset delete modal
    resetDeleteModal() {
      this.forceDeleteRequired = false;
      this.forceDeleteText = '';
      this.deleteStats = { requestCount: 0, balanceCount: 0 };
    },

    // Delete leave type
    async deleteLeaveType() {
      if (!this.deleteItem) return;

      // If force delete required but text doesn't match, don't proceed
      if (this.forceDeleteRequired && this.forceDeleteText !== 'FORCE DELETE') {
        return;
      }

      this.deleting = true;

      const data = new FormData();
      data.append("function", "deleteLeaveType");
      data.append("id", this.deleteItem.id);

      // Include confirmText if force delete
      if (this.forceDeleteRequired) {
        data.append("confirmText", this.forceDeleteText);
      }

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          $('#delete-modal').modal('hide');
          this.showSuccess(res.data.message);
          this.deleteItem = null;
          this.resetDeleteModal();
          await this.fetchLeaveTypes();
        } else {
          // Check if force delete is required
          if (res.data.data?.requiresForceDelete) {
            this.forceDeleteRequired = true;
            this.deleteStats = {
              requestCount: res.data.data.requestCount || 0,
              balanceCount: res.data.data.balanceCount || 0
            };
          } else {
            this.showError(res.data.message);
          }
        }
      } catch (error) {
        console.error('Error deleting leave type:', error);
        this.showError('Failed to delete leave type');
      } finally {
        this.deleting = false;
      }
    },

    // Show success message
    showSuccess(message) {
      this.successMessage = message;
      setTimeout(() => {
        $('#success-modal').modal('show');
      }, 300);
    },

    // Show error message
    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        $('#error-modal').modal('show');
      }, 300);
    }
  }
};
</script>

<style scoped>
.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.avatar-xs {
  width: 2rem;
  height: 2rem;
}

.table th {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  white-space: nowrap;
}

.form-check-input {
  cursor: pointer;
}

.form-check-input:checked {
  background-color: #5b73e8;
  border-color: #5b73e8;
}

.form-switch .form-check-input {
  width: 2.5em;
  height: 1.25em;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.mini-stats-wid {
  transition: transform 0.2s ease;
}

.mini-stats-wid:hover {
  transform: translateY(-2px);
}

.form-control-color {
  height: 38px;
}
</style>